import { useState, useRef, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import { aboutContent, faqs } from '@/mocks/site';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleContactSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qrmvfnahb7382ovea0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料吃瓜',
    url: 'https://heiliao-chigua.com',
    description: '黑料吃瓜专属阵地，汇聚娱乐圈最新黑料吃瓜内容，明星八卦、网红翻车、热门瓜情实时更新。',
    email: aboutContent.contact.email,
    telephone: aboutContent.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座1808室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/heiliao-chigua',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao-chigua.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://heiliao-chigua.com/about' },
    ],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 黑料吃瓜',
    description: '了解黑料吃瓜的品牌故事、口碑声誉与联系方式。国内领先的娱乐爆料聚合平台。',
    url: 'https://heiliao-chigua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料吃瓜',
      url: 'https://heiliao-chigua.com',
    },
  };

  return (
    <>
      <SEOMeta
        title="关于我们"
        description="了解黑料吃瓜的品牌故事、口碑声誉与联系方式。国内领先的娱乐爆料聚合平台，每日新鲜出炉，吃瓜群众必备。"
        keywords="黑料吃瓜,关于我们,品牌介绍,口碑声誉,联系方式,娱乐爆料平台"
        ogTitle="关于我们 - 黑料吃瓜·最新娱乐爆料聚合平台"
        ogDescription="了解黑料吃瓜的品牌故事、口碑声誉与联系方式。国内领先的娱乐爆料聚合平台。"
        schema={[orgSchema, faqSchema, breadcrumbSchema, aboutPageSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: t('about.title') as string }]} />
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t('about.title')}
              </h1>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                国内领先的娱乐爆料聚合平台，为吃瓜群众提供最新最全的娱乐圈资讯
              </p>
            </div>

            <div className="flex flex-col lg:flex-row items-center gap-10 mb-16">
              <div className="flex-1">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">
                  {t('about.brandIntro')}
                </h2>
                {aboutContent.brandIntro.paragraphs.map((p, i) => (
                  <p key={i} className="text-foreground-600 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </div>
              <div className="flex-1">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={aboutContent.brandIntro.image}
                    alt="黑料吃瓜品牌介绍"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {aboutContent.reputation.stats.map((stat, i) => (
                <div key={i} className="bg-background-100 rounded-2xl p-6 text-center border border-background-200/60">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              {t('about.reputation')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutContent.reputation.testimonials.map((testimonial, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-6 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 leading-relaxed mb-4 text-sm">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">{testimonial.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              {t('about.faq')}
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="font-heading text-base font-semibold text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              {t('about.contact')}
            </h2>
            <p className="text-foreground-500 text-center mb-10 max-w-2xl mx-auto">
              {aboutContent.contact.description}
            </p>

            <div className="flex flex-col lg:flex-row gap-10 max-w-5xl mx-auto">
              <div className="flex-1 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">邮箱</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">电话</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">地址</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-200 text-foreground-500 flex-shrink-0">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-1">工作时间</p>
                    <p className="text-sm text-foreground-500">{aboutContent.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  ref={formRef}
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="bg-background-50 rounded-2xl p-6 border border-background-200/60 space-y-4"
                >
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot"
                  />
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（不超过500字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-4 py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">留言发送成功！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}